import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square, Sparkles, X, AlertTriangle, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";

const VOICE_COMMANDS = {
  "new patient": "/patients",
  "open referrals": "/referrals",
  "show prior auths": "/prior-auth",
  "generate appeal": "/revenue-cycle",
  "show claims": "/revenue-cycle",
  "open charting": "/charting",
  "open dashboard": "/dashboard",
  "open analytics": "/analytics",
};

export default function VoiceButton() {
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const recogRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    let final = "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          final += t + " ";
          // Voice command check
          const cmd = t.toLowerCase().trim();
          for (const k of Object.keys(VOICE_COMMANDS)) {
            if (cmd.includes(k)) {
              toast.success(`Command: ${k}`);
              navigate(VOICE_COMMANDS[k]);
            }
          }
        } else { interim += t; }
      }
      setTranscript(final + interim);
    };
    r.onerror = (e) => { /* ignore */ };
    r.onend = () => setListening(false);
    recogRef.current = r;
  }, [navigate]);

  const start = () => {
    if (!recogRef.current) return toast.error("Voice not supported in this browser");
    setTranscript(""); setResult(null);
    try { recogRef.current.start(); setListening(true); } catch {}
  };

  const stop = () => {
    if (recogRef.current) recogRef.current.stop();
    setListening(false);
  };

  const analyze = async () => {
    if (!transcript.trim()) return toast.error("No transcript to analyze");
    setProcessing(true);
    try {
      const data = await aiJob("/voice/process", { transcript_text: transcript, session_type: "ambient" });
      let parsed = data;
      if (typeof data.final === "string") {
        try {
          let s = data.final.trim();
          if (s.startsWith("```")) { s = s.split("```",2)[1]; if (s.startsWith("json")) s = s.slice(4); s = s.rsplit ? s.rsplit("```",1)[0] : s.split("```")[0]; }
          parsed = JSON.parse(s);
        } catch { parsed = { summary: data.final }; }
      }
      setResult(parsed);
      toast.success("Voice analyzed");
    } catch (e) { toast.error("Voice analysis failed"); }
    finally { setProcessing(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-white border border-brand hover:border-brand-primary shadow-lg flex items-center justify-center text-brand-primary transition-transform hover:scale-110"
        data-testid="voice-fab"
        title="Voice Intelligence Engine - ambient capture">
        <Mic className="w-5 h-5" />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={()=>setOpen(false)}>
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl border border-brand max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="voice-panel">
            <div className="flex items-center justify-between p-5 border-b border-brand">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${listening?"bg-red-500 animate-pulse":"bg-brand-primary"}`}>
                  {listening ? <MicOff className="w-4 h-4 text-white" /> : <Mic className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <div className="text-base font-semibold">Voice Intelligence Engine</div>
                  <div className="text-[11px] text-brand-muted">Ambient capture + topology analysis</div>
                </div>
              </div>
              <button onClick={()=>setOpen(false)} className="p-1.5 hover:bg-brand-bg rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-2">
                {!listening ? (
                  <button onClick={start} className="bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2" data-testid="voice-start">
                    <Mic className="w-4 h-4" /> Start Listening
                  </button>
                ) : (
                  <button onClick={stop} className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2" data-testid="voice-stop">
                    <Square className="w-4 h-4" /> Stop
                  </button>
                )}
                <button onClick={analyze} disabled={processing || !transcript.trim()} className="bg-brand-teal text-white px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="voice-analyze">
                  <Sparkles className="w-4 h-4" /> {processing?"Analyzing…":"Analyze"}
                </button>
              </div>
              <textarea value={transcript} onChange={(e)=>setTranscript(e.target.value)} rows={6} placeholder="Live transcript appears here…"
                className="w-full px-3 py-2 bg-brand-bg border border-brand rounded-md text-sm" data-testid="voice-transcript" />

              {result && (
                <div className="space-y-3" data-testid="voice-result">
                  {result.risk_flags?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2 items-start">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                      <div>
                        <div className="text-xs font-semibold text-red-700">RISK FLAGS</div>
                        <ul className="text-sm text-red-700 mt-1">
                          {result.risk_flags.map((f,i)=>(<li key={i}>• {f}</li>))}
                        </ul>
                      </div>
                    </div>
                  )}
                  {result.summary && (
                    <div className="text-sm leading-relaxed bg-brand-bg p-3 rounded-md">{result.summary}</div>
                  )}
                  <ChipRow label="ICD-10" items={(result.icd10_suggestions||[]).map(c=>`${c.code} • ${c.description}`)} />
                  <ChipRow label="CPT" items={(result.cpt_suggestions||[]).map(c=>`${c.code} • ${c.description}`)} />
                  <ChipRow label="Referrals detected" items={(result.referrals||[]).map(r=>`${r.specialty} — ${r.diagnosis}`)} />
                  <ChipRow label="Prior Auths" items={(result.prior_auths||[]).map(p=>`${p.procedure} (${p.cpt}) → ${p.payer}`)} />
                  <ChipRow label="Medications" items={(result.medications||[]).map(m=>`${m.drug} ${m.dose} ${m.frequency}`)} />
                  <ChipRow label="Follow-ups" items={(result.follow_ups||[]).map(f=>`${f.timeframe}: ${f.reason}`)} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const ChipRow = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="eyebrow mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((t,i)=>(<span key={i} className="px-2.5 py-1 rounded-md bg-brand-bg border border-brand text-xs">{t}</span>))}
      </div>
    </div>
  );
};
